/**
 * Local-dev fallbacks for storage paths that don't resolve locally.
 *
 * Why
 * ---
 * Postgres dumps capture the `storage_path` *column* but never the
 * underlying Storage *binaries* (those live in S3 / Cloudflare R2 /
 * Supabase Storage, not Postgres). After a fresh staging-to-local
 * sync the local DB has 70K+ media rows, but the local Storage bucket
 * is empty, so every `createSignedUrl(...)` returns a URL that 404s.
 *
 * This helper offers two fallback strategies, chosen at runtime by env
 * var. Both are NO-OP in production (env vars are local-only).
 *
 * Strategy 1 — Public CDN rewrite (cheap, partial coverage)
 * ---------------------------------------------------------
 *   NEXT_PUBLIC_STORAGE_CDN_FALLBACK_BASE=https://cdn.ballee.co
 *
 * Rewrites bucket-relative paths (`<uuid>/<segment>/<file>`) to a
 * public CDN URL. Works only for the ~33% of artist_media rows
 * already migrated to R2 (the rest 404 on the CDN). Cheap and
 * stateless — no calls to any Supabase project.
 *
 * Strategy 2 — Dev storage proxy (heavier, full coverage)
 * -------------------------------------------------------
 *   NEXT_PUBLIC_STORAGE_PROXY_ENABLED=true
 *   STORAGE_PROXY_TARGET_URL=https://<ref>.supabase.co
 *   STORAGE_PROXY_TARGET_SERVICE_ROLE_KEY=...
 *
 * Rewrites to `/api/dev-storage-proxy/<bucket>/<path>` which mints a
 * signed URL against the configured target Storage and 302-redirects.
 * The target is PROD Storage by default (the only Supabase Storage
 * that actually holds the Ballee binaries — staging Storage was
 * verified metadata-only 2026-05-16, see
 * `WIP_local_dev_storage_objects_2026_05_11`). Requires the target
 * service-role key in `.env.local`. The proxy route is locked out in
 * production builds via a two-layer guard.
 *
 * Both can be set simultaneously; the proxy wins because it covers
 * strictly more rows.
 */

const FALLBACK_BASE = process.env.NEXT_PUBLIC_STORAGE_CDN_FALLBACK_BASE;
const PROXY_ENABLED = process.env.NEXT_PUBLIC_STORAGE_PROXY_ENABLED === 'true';

// Maps internal Supabase bucket name → CDN prefix path. Verified
// against staging URL samples (cdn.ballee.co/artist-media/...,
// cdn.ballee.co/orgs/...). Add buckets here if more are surfaced
// to local dev later.
const BUCKET_TO_CDN_PREFIX: Record<string, string> = {
  'dancer-media': 'artist-media',
  'organization-media': 'orgs',
  'profile-media': 'profile-media',
};

/**
 * UUID/<segment>/... path shape. Anything else (legacy meteor-media
 * paths, raw filenames, weird shapes) is left alone so the existing
 * signed-URL fallback can still try to resolve it.
 */
const BUCKET_RELATIVE_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[a-z]+\//i;

/**
 * If `path` is a bucket-relative storage path AND a fallback strategy
 * is enabled via env var, return the resolved URL. Otherwise return
 * `null` (caller should fall through to signed-URL resolution).
 *
 * Strategy precedence: proxy > CDN. The proxy covers strictly more
 * rows, so when both env vars are set we route through the proxy.
 */
export function resolveLocalCdnFallback(
  bucket: string,
  path: string,
): string | null {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return null;

  // Strategy 2: dev-storage-proxy (route-relative URL). The proxy mints a
  // signed URL against prod Storage for ANY object, so it needs no path-shape
  // guard — full coverage regardless of bucket layout (marketplace's
  // `marketplace-drafts/<ts>/...`, legacy meteor paths, uuid-prefixed, etc.).
  if (PROXY_ENABLED) {
    return `/api/dev-storage-proxy/${bucket}/${path}`;
  }

  // Strategy 1: public CDN rewrite. Only the ~33% of media already on R2 (a
  // known bucket + uuid-relative shape) resolve on the public CDN; anything
  // else must fall through to the local signed-URL attempt.
  if (FALLBACK_BASE) {
    if (!BUCKET_RELATIVE_RE.test(path)) return null;
    const prefix = BUCKET_TO_CDN_PREFIX[bucket];
    if (!prefix) return null;
    return `${FALLBACK_BASE.replace(/\/+$/, '')}/${prefix}/${path}`;
  }

  return null;
}

/**
 * Convenience for the public-dancer loader.
 */
export function resolveDancerMediaCdnFallback(path: string): string | null {
  return resolveLocalCdnFallback('dancer-media', path);
}
